/**
 * Full detail view content for task cards (two lines: title+badges, assignees+indicators)
 * Extracted from BoardV3TaskBoxContent.tsx for readability
 */

import type { Task, Employee } from '../../../types';
import { useTaskCategories } from '../../../stores/hooks';
import { TaskBoxWeekBadges } from './TaskBoxWeekBadges';
import { TaskBoxAssigneeList } from './TaskBoxAssigneeList';
import { TaskBoxIndicators } from './TaskBoxIndicators';

interface FullContentProps {
  task: Task;
  assignees: Employee[];
  primaryAssignee: Employee | null;
  hasTeam: boolean;
  effectiveWeeks: number | null;
  baseWeeks: number | null;
  bestCaseWeeksRounded: number | null;
  isMultitasking: boolean;
  teamWeeks?: number;
  riskLevel: "high" | "medium" | "low" | null;
  totalRisk?: number;
  cardWidth: number;
}

export function FullContent({
  task,
  assignees,
  primaryAssignee,
  hasTeam,
  effectiveWeeks,
  baseWeeks,
  bestCaseWeeksRounded,
  isMultitasking,
  teamWeeks,
  riskLevel,
  totalRisk,
  cardWidth,
}: FullContentProps) {
  const allCategories = useTaskCategories();
  const taskCategories = (task.categoryIds ?? [])
    .map(id => allCategories.find(c => c.id === id))
    .filter((c): c is NonNullable<typeof c> => c != null);

  return (
    <>
      {/* Title row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 4 }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            lineHeight: '14px',
          }}
          title={task.title}
        >
          {task.title}
        </span>
        {primaryAssignee && effectiveWeeks !== null && (
          <TaskBoxWeekBadges
            effectiveWeeks={effectiveWeeks}
            baseWeeks={baseWeeks}
            bestCaseWeeksRounded={bestCaseWeeksRounded}
            isMultitasking={isMultitasking}
          />
        )}
      </div>

      {/* Category chips */}
      {taskCategories.length > 0 && (
        <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', overflow: 'hidden', maxHeight: 16 }}>
          {taskCategories.slice(0, 3).map(cat => (
            <span key={cat.id} style={{
              fontSize: 8,
              padding: '0 4px',
              borderRadius: 9999,
              backgroundColor: cat.color + '30',
              border: `1px solid ${cat.color}60`,
              lineHeight: '14px',
              whiteSpace: 'nowrap',
            }}>
              {cat.name}
            </span>
          ))}
          {taskCategories.length > 3 && (
            <span style={{ fontSize: 8, opacity: 0.7, lineHeight: '14px' }}>+{taskCategories.length - 3}</span>
          )}
        </div>
      )}

      {/* Bottom row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
        {primaryAssignee ? (
          <TaskBoxAssigneeList
            assignees={assignees}
            primaryAssignee={primaryAssignee}
            hasTeam={hasTeam}
            teamWeeks={teamWeeks}
            cardWidth={cardWidth}
          />
        ) : (
          <span style={{ fontSize: 9, opacity: 0.7 }}>Nicht zugewiesen</span>
        )}

        <TaskBoxIndicators
          riskLevel={riskLevel}
          totalRisk={totalRisk}
          isMultitasking={isMultitasking}
        />
      </div>
    </>
  );
}
